import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { getCookie, setCookie } from '|/helpers/cookies';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import axios from '|/helpers/axios';
import * as sc from './outage-banner.styles';

type OutageBannerConfig = {
  isEnabled?: boolean;
  outageHeader?: string;
  outageDescription?: string;
  outageCookieName?: string;
  excludedPages?: string[];
  includedPages?: string[];
};

type HeaderApiResponse = {
  appSettings?: {
    OutageBanner?: OutageBannerConfig;
  };
};

// Module-level cache to prevent re-fetching on every mount
let cachedConfig: OutageBannerConfig | null | undefined = undefined;
let configPromise: Promise<OutageBannerConfig | null> | null = null;

const fetchOutageBannerConfig = (): Promise<OutageBannerConfig | null> => {
  if (cachedConfig !== undefined) {
    return Promise.resolve(cachedConfig);
  }

  if (!configPromise) {
    configPromise = axios
      .get<HeaderApiResponse>(`${process.env.HEADER_API_URL}/v2/header?keys=appsettings`)
      .then(r => {
        cachedConfig = r.data.appSettings?.OutageBanner || null;
        return cachedConfig;
      })
      .catch(error => {
        console.error('Failed to fetch outage banner config:', error);
        configPromise = null; // allow retry on transient failure
        return null;
      });
  }
  return configPromise;
};

/**
 * Determines if the banner should be displayed on the current page
 * based on regex pattern matching of included and excluded pages.
 *
 * Examples of Purchase UI patterns that could be configured in AppSettings:
 * - "/steps/" - All purchase steps
 * - "/steps/payment-options" - Specific step only
 * - "/steps/(payment-options|down-payment)" - Multiple specific steps
 */
const shouldDisplayOnPage = (
  includedPages: string[] = [],
  excludedPages: string[] = [],
  currentPath: string
): boolean => {
  const includeAllPages = includedPages.length === 0;
  const isCurrentPageIncluded = includedPages.some(pattern => {
    const regex = new RegExp(pattern, 'i');
    return regex.test(currentPath);
  });
  const isIncluded = includeAllPages || isCurrentPageIncluded;

  const isExcluded = excludedPages.some(pattern => {
    const regex = new RegExp(pattern);
    return regex.test(currentPath);
  });

  return isIncluded && !isExcluded;
};

const OutageBanner: React.FC = () => {
  const { pathname } = useLocation();
  const { trackEvent } = useAnalyticsContext();
  const [bannerConfig, setBannerConfig] = useState<OutageBannerConfig | null>(
    cachedConfig !== undefined ? cachedConfig : null
  );
  const [isLoading, setIsLoading] = useState(cachedConfig === undefined);
  const [isVisible, setIsVisible] = useState(true);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    fetchOutageBannerConfig().then(config => {
      setBannerConfig(config);
      setIsLoading(false);
    });
  }, []);

  const header = bannerConfig?.outageHeader || 'SORRY, WE ARE EXPERIENCING AN OUTAGE';
  const description = bannerConfig?.outageDescription || 'Our team is working hard to fix this';
  const dismissCookie = bannerConfig?.outageCookieName || 'carvana-outage-cookie-banner';

  const dismissCookieValue = getCookie(dismissCookie);

  useEffect(() => {
    if (!dismissCookieValue) {
      setCookie(dismissCookie, 'true');
    }
  }, [dismissCookie, dismissCookieValue]);

  const shouldShowBanner = () => {
    if (!bannerConfig?.isEnabled) return false;

    // First check: Only show where PurchaseHeader is shown
    // (not on order-placed or vehicle pages)
    const isPurchaseHeaderVisible =
      !pathname.startsWith('/steps/order-placed') && !pathname.startsWith('/vehicle/');

    if (!isPurchaseHeaderVisible) return false;

    // Second check: Use the AppSettings page targeting
    const includedPages = bannerConfig.includedPages || [];
    const excludedPages = bannerConfig.excludedPages || [];

    return shouldDisplayOnPage(includedPages, excludedPages, pathname);
  };

  const showBanner = shouldShowBanner();

  // Analytics viewed event
  useEffect(() => {
    if (showBanner && !isLoading && dismissCookieValue !== 'false') {
      trackEvent('header - banner - viewed banner', {
        bannerType: 'outage-banner',
        header,
        description,
        context: 'purchase-flow',
        currentPath: pathname
      });
    }
  }, [showBanner, isLoading, dismissCookieValue, header, description, trackEvent, pathname]);

  const handleClose = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    trackEvent('header - banner - dismissed banner', {
      bannerType: 'outage-banner',
      header,
      description,
      context: 'purchase-flow',
      currentPath: pathname
    });

    // Fade out animation
    setIsVisible(false);

    // Set cookie and hide component after animation completes
    setTimeout(() => {
      setCookie(dismissCookie, 'false');
      setIsHidden(true);
    }, 300);
  };

  const bannerDismissed = dismissCookieValue === 'false';

  if (isLoading || !showBanner || bannerDismissed || isHidden) {
    return null;
  }

  return (
    <sc.OutageBannerWrapper data-testid="purchase-outage-banner" $isVisible={isVisible}>
      <sc.BannerContent>
        <sc.BannerHeader>
          <sc.BannerIcon />
          {header}
        </sc.BannerHeader>
        <sc.BannerDescription>{description}</sc.BannerDescription>
      </sc.BannerContent>
      <sc.CloseButton onClick={handleClose} aria-label="Dismiss banner" type="button">
        X
      </sc.CloseButton>
    </sc.OutageBannerWrapper>
  );
};

export default OutageBanner;
